import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthResponse, SessionUser } from "@rbim/shared";
import { apiRequest, setAuthenticationFailureHandler, setCsrfToken } from "./api";

type AuthContextValue = {
  user: SessionUser | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  refreshUser(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setAuthenticationFailureHandler(() => {
      setCsrfToken(null);
      setUser(null);
    });
    return () => setAuthenticationFailureHandler(null);
  }, []);

  useEffect(() => {
    apiRequest<AuthResponse>("/auth/me")
      .then((response) => { setCsrfToken(response.csrfToken); setUser(response.user); })
      .catch(() => { setCsrfToken(null); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    async login(username, password) {
      const response = await apiRequest<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      setCsrfToken(response.csrfToken);
      setUser(response.user);
    },
    async logout() {
      try { await apiRequest<void>("/auth/logout", { method: "POST" }); }
      finally { setCsrfToken(null); setUser(null); }
    },
    async refreshUser() {
      const response = await apiRequest<AuthResponse>("/auth/me");
      setCsrfToken(response.csrfToken);
      setUser(response.user);
    },
  }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
